import { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { authService } from '../services'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { usePageTitle, setDocumentTitle } from '../utils/translation'
import GoogleAuthButton from '../components/auth/GoogleAuthButton'

export default function Login() {
  const { t } = useTranslation()
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('phone')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showRequired, setShowRequired] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [smsProvider, setSmsProvider] = useState<'smsmkt' | 'thaibulksms'>('smsmkt')
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  
  // Get translated page title
  const pageTitle = usePageTitle('login.title', 'Log In')
  
  // Extract returnTo from location state or URL query params
  const returnToFromState = location.state?.returnTo
  const returnToFromQuery = searchParams.get('returnTo')
  const returnTo = returnToFromState || returnToFromQuery || '/flowers'

  // Handle pre-filled email from location state (from register page)
  const prefilledEmail = location.state?.prefilledEmail

  // On mount, check if user already has a valid token
  useEffect(() => {
    // Set document title
    setDocumentTitle(pageTitle)
    
    // First check if we're already authenticated
    if (authService.isAuthenticated()) {
      // User has a valid token, redirect to homepage
      navigate('/flowers')
      return
    }
    
    // Handle pre-filled email from location state
    if (prefilledEmail) {
      setEmail(prefilledEmail)
      setLoginMethod('email')
    }
    
    // Clear any existing token if coming from session expired notification
    if (searchParams.get('session_expired') === 'true') {
      localStorage.removeItem('authToken')
      localStorage.removeItem('lastActivityTime')
      toast.error(t('login.sessionExpired'))
    }

    // Check if returning from Google OAuth redirect with an error
    const error = searchParams.get('error')
    if (error) {
      toast.error(decodeURIComponent(error))
    }

    // Check if returning from Google OAuth redirect with auth token in cookie
    // The token is set in an HTTP-only cookie by the server, so we just need to check
    // if we have returned from the OAuth flow and have a valid token in the cookie
    const tokenFromCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('authToken='))
    
    if (tokenFromCookie) {
      // We have a token in the cookie set by the server
      // Save it to localStorage and redirect
      const token = tokenFromCookie.split('=')[1]
      localStorage.setItem('authToken', token)
      localStorage.setItem('lastActivityTime', Date.now().toString())
      navigate(returnTo)
      return
    }

    // Initialize Google Auth
    authService.initGoogleAuth()
  }, [searchParams, navigate, pageTitle, t, returnTo, prefilledEmail]) // Re-run when search params change
  
  const handleBack = () => {
    navigate('/welcome')
  }

  const handleLoginMethodChange = (type: 'email' | 'phone') => {
    setLoginMethod(type)
    setShowRequired(false)
    setError('')
  }

  const handleRequestCode = async () => {
    if (!phone) {
      setShowRequired(true)
      return
    }

    // Basic phone validation
    if (phone.length < 9) {
      setError(t('login.invalidPhoneNumber'))
      return
    }

    setLoading(true)
    setError('')

    try {
      // Add +66 prefix to the phone number
      const fullPhoneNumber = `66${phone}`;
      
      const response = await authService.requestLoginCode(fullPhoneNumber, smsProvider)
      
      // Check if response was not successful
      if (!response.success) {
        setError(response.message)
        return
      }
      
      // Navigate to phone verification page instead of showing verification in this component
      navigate('/phone-verify', { 
        state: { 
          registrationType: 'login',
          contactInfo: fullPhoneNumber,
          returnTo: returnTo
        } 
      })
    } catch (err: any) {
      console.error('Request code error:', err)
      let errorMessage = t('login.verificationCodeError')
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response && err.response.data) {
        // Handle API error responses
        const responseError = err.response.data.error || err.response.data.message;
        if (responseError) {
          errorMessage = responseError;
        }
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Only handle email login form submission
    // Phone login is now handled via PhoneVerify component
    if (loginMethod === 'email') {
      if (!email || !password) {
        setShowRequired(true)
        return
      }
    
      setLoading(true)
      setError('')
    
      try {
        await authService.login(email, password)
        navigate(returnTo)
      } catch (err: any) {
        console.error('Login error:', err)
        
        // Extract error message from the response if available
        let errorMessage = t('login.loginFailed')
        
        if (err.response) {
          const responseError = err.response.data?.error
          
          if (responseError) {
            errorMessage = responseError
          }
        } else if (err.message) {
          errorMessage = err.message
        }
        
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="p-6 flex-1">
        {/* Navbar with circular back button */}
        <div className="flex items-center mb-8">
          <button onClick={handleBack} className="mr-4 p-2 bg-gray-200 rounded-full">
            <FaArrowLeft className="text-gray-700 text-xl font-bold" />
          </button>
        </div>

        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-semibold">{t('login.title')}</h2>
          <Link 
            to={`/register${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
            className="text-sm text-blue-600 font-medium hover:text-blue-800 transition-colors underline"
          >
            {t('login.switchToRegister', 'Sign Up')}
          </Link>
        </div>

        <p className="text-gray-700 text-sm mb-6">
          {t('login.subtitle', { type: loginMethod === 'email' ? t('login.emailAndPassword') : t('login.phoneNumber') })}
        </p>

        {/* Google login button */}
        <GoogleAuthButton 
          mode="login"
          returnTo={returnTo}
          onError={(error) => setError(error.message)}
        />
        
        <div className="flex items-center justify-center mb-4">
          <div className="flex-1 h-[1px] bg-gray-300"></div>
          <span className="mx-3 text-gray-500 text-sm">{t('login.or')}</span>
          <div className="flex-1 h-[1px] bg-gray-300"></div>
        </div>

        {/* Toggle between email and phone login - styled like Register page */}
        <div className="flex mb-6 gap-3">
          <button
            onClick={() => handleLoginMethodChange('phone')}
            className={`flex-1 py-3.5 text-center rounded-[12px] border ${
              loginMethod === 'phone' 
                ? 'border-[#F9D348] bg-[#FFFAEB] text-black font-medium' 
                : 'border-gray-200 bg-white text-gray-500'
            }`}
          >
            {t('login.phone')}
          </button>
          <button
            onClick={() => handleLoginMethodChange('email')}
            className={`flex-1 py-3.5 text-center rounded-[12px] border ${
              loginMethod === 'email' 
                ? 'border-[#F9D348] bg-[#FFFAEB] text-black font-medium' 
                : 'border-gray-200 bg-white text-gray-500'
            }`}
          >
            {t('login.email')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {loginMethod === 'email' ? (
            <>
              <input
                type="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setShowRequired(false)
                }}
                className="w-full p-4 border border-gray-300 rounded-[16px] mb-4 focus:outline-none focus:border-gray-500"
              />
              
              <input
                type="password"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setShowRequired(false)
                }}
                className="w-full p-4 border border-gray-300 rounded-[16px] mb-4 focus:outline-none focus:border-gray-500"
              />
            </>
          ) : (
            <>
              <div className="relative w-full mb-1">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-700 font-medium">+66</div>
                <input
                  type="tel"
                  placeholder={t('login.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => {
                    // Remove any non-digit characters
                    const value = e.target.value.replace(/\D/g, '');
                    setPhone(value);
                    setShowRequired(false);
                  }}
                  className="w-full p-4 pl-14 border border-gray-300 rounded-[16px] focus:outline-none focus:border-gray-500"
                />
              </div>
              <p className="text-gray-500 text-xs mb-2">{t('login.thaiPhoneOnly')}</p>
              
              {/* Phone number change help */}
              <div className="mb-4 text-center">
                <Link 
                  to="/contact-us?from=phone-change" 
                  className="text-xs text-gray-600 hover:text-gray-800 transition-colors underline"
                >
                  {t('login.phoneChangeHelp', 'I changed my phone number and cannot login')}
                </Link>
              </div>
              
              {/* Remove SMS Provider Switcher UI */}
              
              <button 
                type="button"
                onClick={handleRequestCode}
                disabled={loading || !phone}
                className={`w-full h-[60px] rounded-[16px] font-medium text-black ${
                  phone ? 'bg-[#F9D348]' : 'bg-[#FDEDB6]'
                }`}
              >
                {loading ? t('login.sending') : t('login.sendVerificationCode')}
              </button>
            </>
          )}
          
          {showRequired && (
            <span className="text-red-500 text-sm mb-4">
              {loginMethod === 'email' 
                ? t('login.emailPasswordRequired') 
                : t('login.phoneRequired')}
            </span>
          )}
          
          {error && (
            <span className="text-red-500 text-sm mb-4">{error}</span>
          )}

          {loginMethod === 'email' && (
            <button 
              type="submit"
              disabled={loading}
              className={`w-full h-[60px] rounded-[16px] font-medium text-black mt-6 ${
                (email && password) ? 'bg-[#F9D348]' : 'bg-[#FDEDB6]'
              }`}
            >
              {loading ? t('login.processing') : t('login.loginButton')}
            </button>
          )}
          
          {loginMethod === 'email' && (
            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-blue-600 text-sm">
                {t('login.forgotPassword')}
              </Link>
            </div>
          )}
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {t('login.noAccount')}{' '}
            <Link 
              to={`/register${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              className="text-blue-600 font-medium hover:text-blue-800 transition-colors underline"
            >
              {t('login.createAccount')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
} 